/**
 * What stops one bulk card from being scheduled. The set mirrors the single
 * form's "Save as Scheduled" guards in useWorkOrderForm (bean, a lot with a
 * weight, roast type, stock, output ≤ input) — the two modes must agree on
 * what a schedulable work order is.
 */
export type BulkCardIssue = 'bean' | 'lots' | 'roastType' | 'insufficientStock' | 'outputExceedsInput';

export interface BulkCardState {
  selectedBeanId: string;
  /** Lot rows with a lot picked and a used weight > 0. */
  validLotCount: number;
  roastType: string;
  inputWeight: number;
  outputWeight: number;
  hasInsufficientStock: boolean;
  isOutputExceedsInput: boolean;
}

export interface BulkCardEvaluation {
  isComplete: boolean;
  /** Only reported for a card the user started filling in. An untouched card
   *  is not an error: it stays a draft, which the batch confirm modal says. */
  issues: BulkCardIssue[];
}

export const evaluateBulkCard = (state: BulkCardState): BulkCardEvaluation => {
  const all: BulkCardIssue[] = [];
  if (!state.selectedBeanId) all.push('bean');
  if (state.validLotCount === 0) all.push('lots');
  if (!state.roastType.trim()) all.push('roastType');
  if (state.hasInsufficientStock) all.push('insufficientStock');
  if (state.isOutputExceedsInput) all.push('outputExceedsInput');

  // Touching any schedule field means the user meant to schedule this card, so
  // a gap is a mistake to fix, not a silent "leave it as draft". Lots cannot
  // be picked without a bean, so the bean covers them.
  const isStarted =
    !!state.selectedBeanId || !!state.roastType.trim() || state.inputWeight > 0 || state.outputWeight > 0;

  return {
    isComplete: all.length === 0,
    issues: isStarted ? all : [],
  };
};
